'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Accordion } from '@/components/accordion/Accordion';
import { Headline } from '@/components/headline/Headline';
import clsx from 'clsx';
import { Badge } from '@/components/badge/Badge';
import { AccordionProps } from './types';
import { renameDomProps } from '@/utils/sanitizeDomProps';

export interface ExtendedAccordionListProps {
  title?: string;
  items?: AccordionProps[];
  variant?: 'primary' | 'secondary' | 'tertiary';
  theme?: 'light' | 'dark';
  sectionBackgroundColor?: string;
  spacingTop?: boolean;
  spacingBottom?: boolean;
  badge?: React.ComponentProps<typeof Badge>;
  openAccordionIndex?: number | null;
  onAccordionToggle?: (index: number) => void;
  defaultOpenIndex?: number | null;
  selectedTag?: string;
  allowMultiple?: boolean;
  iconVariant?: 'arrow' | 'plus';
}

export function AccordionList({
  title,
  items = [],
  variant = 'primary',
  theme = 'light',
  sectionBackgroundColor,
  spacingTop,
  spacingBottom,
  badge,
  defaultOpenIndex = 0,
  selectedTag,
  allowMultiple = false,
  iconVariant = 'arrow',
  ...rest
}: ExtendedAccordionListProps) {
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(
    null,
  );
  const [openAccordionIndexes, setOpenAccordionIndexes] = useState<number[]>(
    [],
  );
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const renamedProps = renameDomProps(rest);

  // Check if the viewport is mobile
  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkViewport(); // Initial check
    window.addEventListener('resize', checkViewport);

    return () => {
      window.removeEventListener('resize', checkViewport);
    };
  }, []);

  useEffect(() => {
    if (selectedTag === 'Anlagen' && !isMobile) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (defaultOpenIndex !== null) {
              if (allowMultiple) {
                setOpenAccordionIndexes([defaultOpenIndex]);
              } else {
                setOpenAccordionIndex(defaultOpenIndex);
              }
            }
            setIsFirstTime(false);
          }
        },
        { threshold: 0.5 },
      );

      const currentSectionRef = sectionRef.current;

      if (currentSectionRef) {
        observer.observe(currentSectionRef);
      }

      return () => {
        if (currentSectionRef) {
          observer.unobserve(currentSectionRef); // Ensure proper cleanup
        }
        observer.disconnect();
      };
    }
  }, [
    selectedTag,
    sectionRef,
    defaultOpenIndex,
    isMobile,
    isFirstTime,
    allowMultiple,
  ]);

  const handleAccordionToggle = (index: number) => {
    if (!allowMultiple) {
      // single-open
      setOpenAccordionIndex((prev) => (prev === index ? null : index));
      return;
    }
    // multiple-open
    setOpenAccordionIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <div
      ref={sectionRef}
      data-component-id="C002"
      className={clsx(
        'smzh-accordion-list relative',
        theme === 'light' ? 'text-brand-700' : 'text-white',
        `bg-${sectionBackgroundColor}`,
        `${
          spacingTop ? 'pt-4 md:pt-8 lg:pt-20' : ''
        } ${spacingBottom ? 'pb-4 md:pb-8 lg:pb-20' : ''}`,
        variant === 'tertiary'
          ? 'mx-auto w-full max-w-screen-xl'
          : 'container mx-auto',
      )}
      {...renamedProps}
    >
      <div
        className={clsx(
          'mx-auto flex flex-col',
          variant === 'tertiary'
            ? clsx(
                'overflow-hidden rounded-xl border',
                theme === 'dark'
                  ? 'border-white/10 bg-brand-700'
                  : 'border-neutral-100 bg-white',
              )
            : 'gap-3 sm:gap-6',
        )}
      >
        {badge && <Badge {...badge} />}
        {title && (
          <Headline
            level="h3"
            variant="headingThree"
            title={title}
            spacing={'none'}
            className={clsx(variant === 'tertiary' && 'p-4')}
          />
        )}
        {items.map((item, index) => (
          <Accordion
            key={index}
            {...item}
            theme={theme}
            variant={variant}
            index={index}
            isOpen={
              allowMultiple
                ? openAccordionIndexes.includes(index)
                : openAccordionIndex === index
            }
            iconVariant={iconVariant}
            onToggle={() => handleAccordionToggle(index)}
          />
        ))}
      </div>
    </div>
  );
}
